import { useEffect } from "react";

function Info() {
  useEffect(() => {
    // === Animate rate cards with stagger ===
    const rateCards = document.querySelectorAll(".rate-card");
    const rateObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Array.from(rateCards).indexOf(entry.target);
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add("show");
              rateObserver.unobserve(entry.target);
            }, idx * 150);
          }
        });
      },
      { threshold: 0.2 }
    );
    rateCards.forEach((card) => rateObserver.observe(card));

    // === Animate penalty cards with stagger ===
    const penaltyCards = document.querySelectorAll(".penalty-card");
    const penaltyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const idx = Array.from(penaltyCards).indexOf(entry.target);
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add("show");
              penaltyObserver.unobserve(entry.target);
            }, idx * 150);
          }
        });
      },
      { threshold: 0.2 }
    );
    penaltyCards.forEach((card) => penaltyObserver.observe(card));

    return () => {
      rateObserver.disconnect();
      penaltyObserver.disconnect();
    };
  }, []);

  return (
    <>
      <section className="bg-default text-center big-section">
        {/* === HERO SECTION === */}

        <div className="container px-4 px-lg-5">
          <h1 className="text-white mb-3 big-title">CGT INFO PAGE</h1>
          <p className="text-white-80 lead">
            We know learning about CGT isn’t the most exciting use of your time.
            That’s why we learn the ins and outs: so you don’t have to.
            <br />
            <br />
            But, if you want to know more about CGT, here’s a more detailed
            overview:
          </p>
        </div>

        {/* === WHAT IS CGT === */}
        <div className="info-section text-start">
          <div className="container px-4 px-lg-5">
            <h2 className="section-title text-white mb-4">
              What is Capital Gains Tax (CGT)?
            </h2>
            <p className="text-white-80">
              As of 6 April 2020, HMRC requires individuals to report and pay
              any CGT due within 60 days of completing the sale of a UK
              residential property.
            </p>
            <p className="text-white-80">
              While "disposing of an asset" can include gifting, transferring,
              or selling various types of assets, our service focuses
              specifically on helping you meet your CGT File & Pay obligations
              when selling a property.
            </p>
            <p className="text-white-80">
              If you haven’t sold a property and want to understand other
              scenarios that may trigger a CGT liability, you can learn more on
              the official government website: <br /> 👉
              <a
                href="https://www.gov.uk/capital-gains-tax"
                target="_blank"
                rel="noreferrer"
                className="text-accent"
              >
                https://www.gov.uk/capital-gains-tax
              </a>
            </p>
          </div>
        </div>

        {/* === CGT RATES === */}
        <div className="rates-section">
          <div className="container px-4 px-lg-5 text-center">
            <h2 className="section-title text-white mb-4">CGT RATES</h2>
            <p className="text-white-80 mb-4">
              One of the factors used in calculating CGT is your income.
              Currently, there are 5 rates that CGT is charged at depending on
              income and what asset is being disposed of. Since we are focusing
              on calculating CGT for you if you’ve sold a property, the 18 and
              28% are the most relevant rates:
            </p>
            <div className="rate-cards">
              <div className="rate-card">
                14% where business asset disposal relief is available, up from 10% previously
              </div>
              <div className="rate-card">
                18% on gains on residential property made by basic rate
                taxpayers
              </div>
              <div className="rate-card">
                24% on gains on residential property made by higher rate taxpayers, down from 28% previously 
              </div>
              <div className="rate-card">
                18% on most other gains made by basic rate taxpayers 
              </div>
              <div className="rate-card">
               24% on most other gains made by higher rate taxpayers 
              </div>
              <div className="rate-card">
                Private Residence Relief
                <br />
                If the property is your main home, you may be eligible for
                Private Residence Relief — reducing the CGT you pay.
              </div>
            </div>
            <p className="text-white-80 mt-4">
              It is also important to note that these rates only apply to the
              taxable gain, NOT the total income. The Annual Exempt Amount
              allows a certain portion of your gain to be taken free of tax. For
              this 2025/26 tax year, the Annual Exemption is £3,000.
            </p>
              
              <p className="text-white-80 mt-4">
              If you do
              not inform HMRC when changes happen that affect your liability to
              tax, there is likely to be a penalty. For example, selling an
              property that’s made a capital gain and not filing & paying it
            could result in a penalty of upto 100% of the tax due.
            </p>
          </div>
        </div>

        {/* === PENALTIES === */}
        <div className="penalty-section">
          <div className="container px-4 px-lg-5">
            <h2 className="section-title text-white mb-4">
              <b>HMRC’s Penalties</b>
            </h2>
            <p className="text-white-80 mb-4">
              With only 60 days from the point of sale to file & pay your CGT,
              you can never be too careful that all your documents and
              calculations are accurate to avoid penalties. There are currently
              3 types: latesection penalties, inaccuracy penalties and failure
              to notify penalties.
            </p>
            <div className="penalty-cards">
              <div className="penalty-card">
                <h5 className="text-white">
                  <b>LATE PENALTIES:</b>
                </h5>
                <p className="text-white-80">
                  If a return is filed MORE THAN 60 DAYS from the point of sale,
                  there is a late penalty of £100. If a return is filed MORE
                  THAN 6 MONTHS from the point of sale, there is a late penalty
                  of £300 OR 5% of the outstanding tax, whichever is higher. If
                  a return is filed MORE THAN 12 MONTHS from the point of sale,
                  there is also a late penalty of £300 or 5% of the outstanding
                  tax, whichever is higher.
                </p>
              </div>
              <div className="penalty-card">
                <h5 className="text-white">
                  <b>INACCURACY PENALTIES:</b>
                </h5>
                <p className="text-white-80">
                  If a penalty arises DUE TO LACK OF REASONABLE CARE, the
                  penalty will be between 0 and 30% of the extra tax due. If the
                  ERROR IS DELIBERATE, the penalty will be between 20 and 70% of
                  the extra tax due. If the ERROR IS DELIBERATE AND CONCEALED,
                  the penalty will be between 30 and 100% of the extra tax due.
                </p>
              </div>
              <div className="penalty-card">
                <h5 className="text-white">
                  <b>FAILURE TO NOTIFY PENALTY:</b>
                </h5>
                <p className="text-white-80">
                  If you do not inform HMRC when changes happen that affect your
                  liability to tax, there could be a penalty. For example,
                  selling an asset that’s made a capital gain and not filing &
                  paying it could result in a penalty. This penalty is
                  calculated based on the amount of tax that’s been unpaid as a
                  result of the failure to notify.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Info;
